import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, AlertCircle, Calendar, CheckCircle, Clock, Zap, X, FileText, ListChecks, HandCoins, Users, ScrollText, Download, Mic, Ticket, MapPin, Palette, Wallet, User, Music, UtensilsCrossed, Headphones, Tag, Building2, Mail, Phone } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiUrl, readFailedApiResponse } from '../lib/apiBaseUrl';
import { supabase } from '../lib/supabaseClient';
import { canonicalizeBookingStatus, createAdminBookingNotification } from '../lib/bookingService';
import { resolveBookingWorkflowStage } from '../lib/bookingStage';
import { getFinalStageHelperText, getFinalStageLabel } from '../lib/bookingListFilters';
import {
  CONSULTATION_STATUSES,
  consultationIsoFromLocal,
  formatConsultationDateTime,
  getConsultationStatusDescription,
  getConsultationStatusLabel,
  normalizeConsultationStatus,
  toDatetimeLocalValue,
} from '../lib/consultationSchedule';
import {
  CONTRACT_BUCKET,
  CONTRACT_REVIEW_STATUSES,
  CONTRACT_TEMPLATE_TYPE,
  PROPOSAL_REVIEW_STATUSES,
  SIGNED_CONTRACT_TYPE,
  canClientReplaceSignedContract,
  getContractReviewLabel,
  getContractReviewStatus,
  getContractTemplate,
  getOfficialContractTitle,
  getStoredContract,
  hasSignedContract,
  isContractAccepted,
  isPdfFile,
  isProposalContractComplete,
  isProposalContractViewOnly,
  isProposalDetailsApproved,
  pickProposalEditValues,
  shouldShowProposalContractIcon,
  upsertAttachment,
} from '../lib/proposalContract';

const timelineStages = {
  request_submitted: {
    label: 'Request Submitted',
    icon: CheckCircle,
    color: 'bg-blue-500',
  },
  admin_review: {
    label: 'Admin Review',
    icon: Clock,
    color: 'bg-amber-500',
  },
  initial_consultation: {
    label: 'Initial Consultation',
    icon: AlertCircle,
    color: 'bg-purple-500',
  },
  proposal_contract: {
    label: 'Proposal & Contract',
    icon: FileText,
    color: 'bg-purple-500',
  },
  event_preparation: {
    label: 'Event Preparation',
    icon: AlertCircle,
    color: 'bg-fuchsia-500',
  },
  event_live: {
    label: 'Event is Now Live!',
    icon: Zap,
    color: 'bg-green-500',
  },
};

const statusToStage = {
  new: 'request_submitted',
  pending_review: 'admin_review',
  in_review: 'admin_review',
  approved: 'event_preparation',
  accepted: 'event_preparation',
  initial_consultation: 'initial_consultation',
  proposal_quotation: 'proposal_contract',
  agreement_confirmation: 'proposal_contract',
  contract_signing: 'proposal_contract',
  proposal_contract: 'proposal_contract',
  event_preparation: 'event_preparation',
  event_live: 'event_live',
  declined: 'declined',
};

const AGREEMENT_TERMS = [
  {
    icon: FileText,
    text: 'Event requirements and scope of services',
  },
  {
    icon: ListChecks,
    text: 'Schedule, venue, and production needs',
  },
  {
    icon: HandCoins,
    text: 'Payment terms and deliverables',
  },
  {
    icon: Users,
    text: 'Roles and responsibilities of both parties',
  },
  {
    icon: ScrollText,
    text: 'Any other terms discussed and agreed upon',
  },
];

const formatDisplayValue = (value, fallback = '—') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const formatEventDateValue = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';

  const dateOnly = text.slice(0, 10);
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(dateOnly)
    ? new Date(`${dateOnly}T00:00:00+08:00`)
    : new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;

  try {
    return parsed.toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return text;
  }
};

const buildSubmittedBookingDetails = (booking) => {
  const isCorporate = String(booking?.booking_type || '').toLowerCase() === 'corporate';
  const vendorChoices = booking?.concert_vendor_choices || {};
  const details = [
    { label: 'Event Name', value: formatDisplayValue(booking?.event_name) },
    { label: 'Booking Type', value: isCorporate ? 'Corporate' : 'Concert' },
  ];

  const eventDate = formatEventDateValue(booking?.event_date);
  if (eventDate) {
    details.push({ label: 'Event Date', value: eventDate });
  }

  if (isCorporate) {
    details.push(
      { label: 'Event Type', value: formatDisplayValue(booking?.event_type) },
      { label: 'Number of Attendees', value: formatDisplayValue(booking?.attendee_count) },
      { label: 'Company Name', value: formatDisplayValue(booking?.company_name) },
    );
  }

  details.push(
    { label: 'Venue', value: formatDisplayValue(booking?.venue || (vendorChoices.venue === 'let_echo_jham' ? 'Let EchoJham provide' : '')) },
    { label: 'Theme', value: formatDisplayValue(booking?.theme) },
    { label: 'Budget Range', value: formatDisplayValue(booking?.budget_range) },
  );

  if (!isCorporate) {
    details.push(
      { label: 'Technical Rider', value: formatDisplayValue(vendorChoices.technical_rider || 'Let EchoJham Provide') },
      { label: 'Musical Director', value: formatDisplayValue(vendorChoices.musical_director || 'Let EchoJham Provide') },
      { label: 'Food', value: formatDisplayValue(vendorChoices.food || 'Let EchoJham Provide') },
      {
        label: 'Details / Specific Needs',
        value: formatDisplayValue(booking?.details || booking?.specific_needs, 'No additional details provided.'),
        multiline: true,
      },
    );
  } else {
    details.push(
      {
        label: 'Specific Needs',
        value: formatDisplayValue(booking?.specific_needs, 'No specific needs provided.'),
        multiline: true,
      },
      {
        label: 'Details',
        value: formatDisplayValue(booking?.details, 'No additional details provided.'),
        multiline: true,
      },
    );
  }

  details.push(
    { label: 'Requester Name', value: formatDisplayValue(booking?.requester_name) },
    { label: 'Requester Email', value: formatDisplayValue(booking?.requester_email) },
    { label: 'Requester Phone', value: formatDisplayValue(booking?.requester_phone) },
  );

  return details;
};

const submittedDetailIcons = {
  'Event Name': Mic,
  'Booking Type': Ticket,
  'Event Date': Calendar,
  'Event Type': Tag,
  'Number of Attendees': Users,
  'Company Name': Building2,
  Venue: MapPin,
  Theme: Palette,
  'Budget Range': Wallet,
  'Technical Rider': User,
  'Musical Director': Music,
  Food: UtensilsCrossed,
  'Details / Specific Needs': FileText,
  'Specific Needs': FileText,
  Details: FileText,
  'Requester Name': Headphones,
  'Requester Email': Mail,
  'Requester Phone': Phone,
};

const getSubmittedDetailIcon = (label) => submittedDetailIcons[label] || FileText;

const resolveTimelineStage = (booking) => {
  return resolveBookingWorkflowStage(booking, {
    statusToStage,
    fallbackStage: 'request_submitted',
    isKnownStage: (stage) => Boolean(timelineStages[stage]),
  });
};

const formatSubmittedDateTime = (value) => {
  if (!value) return 'Date unavailable';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Date unavailable';

  try {
    return parsed.toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return parsed.toISOString();
  }
};

export default function BookingProgressPage() {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPacModal, setShowPacModal] = useState(false);
  const [proposalForm, setProposalForm] = useState({});
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [isSavingProposal, setIsSavingProposal] = useState(false);
  const [proposalError, setProposalError] = useState('');
  const [contractFile, setContractFile] = useState(null);
  const [isSubmittingContract, setIsSubmittingContract] = useState(false);
  const [contractError, setContractError] = useState('');
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [showBookingDetailsModal, setShowBookingDetailsModal] = useState(false);
  const [consultationDateTime, setConsultationDateTime] = useState('');
  const [isSubmittingConsultation, setIsSubmittingConsultation] = useState(false);
  const [consultationError, setConsultationError] = useState('');
  const [pacPromptedId, setPacPromptedId] = useState('');

  const currentStage = resolveTimelineStage(booking);
  const isDeclined = currentStage === 'declined' || canonicalizeBookingStatus(booking?.status) === 'declined';
  const stageKeys = Object.keys(timelineStages);
  const currentStageIndex = stageKeys.indexOf(currentStage);
  const storedContract = getStoredContract(booking);
  const contractTemplate = getContractTemplate(booking);
  const pacComplete = isProposalContractComplete(booking);
  const pacViewOnly = isProposalContractViewOnly(booking, currentStage);
  const detailsApproved = isProposalDetailsApproved(booking);
  const showPacIcon = shouldShowProposalContractIcon(booking, currentStage);
  const waitingAdminReview = booking?.proposal_review_status === PROPOSAL_REVIEW_STATUSES.PENDING_ADMIN;
  const clientHasAgreed = Boolean(booking?.client_agreement_confirmed || booking?.agreement_confirmed);
  const signedUploaded = hasSignedContract(booking);
  const contractReviewStatus = getContractReviewStatus(booking);
  const contractAccepted = isContractAccepted(booking);
  const detailsEditable = Boolean(booking && !pacViewOnly && !pacComplete && !detailsApproved && !waitingAdminReview);
  const canAgree = Boolean(booking && !pacViewOnly && !clientHasAgreed && detailsApproved);
  const canUploadSigned = Boolean(booking && !pacViewOnly && clientHasAgreed && canClientReplaceSignedContract(booking));
  const consultationStatus = normalizeConsultationStatus(booking?.consultation_status);
  const consultationIsReadOnly = consultationStatus === CONSULTATION_STATUSES.SCHEDULED;

  useEffect(() => {
    if (!booking?.id) return;
    setConsultationDateTime(toDatetimeLocalValue(booking.consultation_scheduled_at));
  }, [booking?.consultation_scheduled_at, booking?.id]);

  useEffect(() => {
    if (!booking?.id) return;
    const pending = booking.proposal_pending_changes || {};
    setProposalForm(pickProposalEditValues({
      ...booking,
      ...pending,
    }));
  }, [booking]);

  useEffect(() => {
    if (!booking?.id || isDeclined) return;
    if (currentStage !== 'proposal_contract' || pacViewOnly || pacComplete) return;
    if (pacPromptedId === booking.id) return;
    setPacPromptedId(booking.id);
    setShowPacModal(true);
  }, [booking?.id, currentStage, isDeclined, pacComplete, pacPromptedId, pacViewOnly]);

  const fetchBooking = useCallback(async () => {
    try {
      if (!bookingId) return;

      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        setBooking({
          ...data,
          status: canonicalizeBookingStatus(data.status),
        });
      } else {
        setError('Booking not found');
      }
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (!bookingId) {
      setError('Booking not found');
      setLoading(false);
      return;
    }

    fetchBooking();

    // Set up real-time subscription
      const channel = supabase
      .channel(`booking:${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `id=eq.${bookingId}`,
        },
        (payload) => {
          if (payload.new) {
            setBooking((prev) => ({
              ...prev,
              ...payload.new,
              status: canonicalizeBookingStatus(payload.new.status),
            }));
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [bookingId, fetchBooking]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto mb-4"></div>
          <p className="text-slate-300">Loading booking progress...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-[calc(100vh-11rem)] text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <p className="text-lg font-bold mb-2">{error || 'Booking not found'}</p>
          <button
            onClick={() => navigate('/book')}
            className="mt-4 px-4 py-2 bg-brand-maroon hover:bg-brand-red text-white rounded-lg"
          >
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const openPacModal = () => {
    if (!booking?.id) return;
    setProposalError('');
    setContractError('');
    setContractFile(null);
    setShowPacModal(true);
  };

  const openConsultationModal = () => {
    if (!booking?.id) return;

    setConsultationError('');
    setConsultationDateTime(toDatetimeLocalValue(booking.consultation_scheduled_at));
    setShowConsultationModal(true);
  };

  const submitConsultationSchedule = async () => {
    if (!booking?.id || isSubmittingConsultation || consultationIsReadOnly) return;

    const scheduledAt = consultationIsoFromLocal(consultationDateTime);
    if (!scheduledAt) {
      setConsultationError('Please choose a valid date and time.');
      return;
    }

    setIsSubmittingConsultation(true);
    setConsultationError('');

    const nowIso = new Date().toISOString();
    const updatePayload = {
      consultation_scheduled_at: scheduledAt,
      consultation_status: CONSULTATION_STATUSES.SUBMITTED,
      updated_at: nowIso,
    };

    const { data, error: updateError } = await supabase
      .from('bookings')
      .update(updatePayload)
      .eq('id', booking.id)
      .select('*')
      .single();

    if (updateError) {
      console.error('Consultation schedule submission failed:', updateError);
      setConsultationError(updateError.message || 'Unable to submit consultation schedule. Please try again.');
      setIsSubmittingConsultation(false);
      return;
    }

    const updatedBooking = {
      ...(booking || {}),
      ...(data || updatePayload),
      status: canonicalizeBookingStatus((data || updatePayload).status || booking?.status),
    };

    setBooking(updatedBooking);
    setIsSubmittingConsultation(false);
    setShowConsultationModal(false);

    try {
      await createAdminBookingNotification(updatedBooking, {
        title: `Consultation schedule submitted: ${updatedBooking.event_name || updatedBooking.request_code || 'Booking request'}`,
        message: `${updatedBooking.requester_name || 'Client'} submitted a preferred consultation time (${formatConsultationDateTime(scheduledAt)}). Booking #${updatedBooking.id}.`,
        meta: `Bookings #${updatedBooking.id}`,
        scope: 'admin',
      });
    } catch (notificationError) {
      console.warn('Unable to notify admin about consultation schedule:', notificationError?.message || notificationError);
    }
  };

  const storeGeneratedContractPdf = async (sourceBooking) => {
    const existing = getContractTemplate(sourceBooking);
    if (existing?.path || existing?.url) {
      return { booking: sourceBooking, attachment: existing };
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    if (!accessToken) {
      throw new Error('Please sign in again to generate the official contract.');
    }

    const response = await fetch(apiUrl('/bookings/generate-contract/'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ booking_id: sourceBooking.id }),
    });

    if (!response.ok) {
      throw new Error(
        await readFailedApiResponse(response, 'Unable to generate the official contract PDF.'),
      );
    }

    const blob = await response.blob();
    if (!blob || blob.size < 8) {
      throw new Error('The official contract PDF was empty.');
    }

    const storagePath = `${sourceBooking.id}/${Date.now()}-EchoJham_Contract_${sourceBooking.request_code || sourceBooking.id}.pdf`;
    const { error: uploadError } = await supabase.storage
      .from(CONTRACT_BUCKET)
      .upload(storagePath, blob, { upsert: true, contentType: 'application/pdf' });
    if (uploadError) throw uploadError;

    const { data: publicUrlData } = supabase.storage.from(CONTRACT_BUCKET).getPublicUrl(storagePath);
    const attachment = {
      type: CONTRACT_TEMPLATE_TYPE,
      name: `EchoJham_Contract_${sourceBooking.request_code || sourceBooking.id}.pdf`,
      path: storagePath,
      url: publicUrlData?.publicUrl || '',
      uploaded_at: new Date().toISOString(),
    };
    const nextAttachments = upsertAttachment(sourceBooking.attachments, attachment);
    const { data, error: updateError } = await supabase
      .from('bookings')
      .update({
        attachments: nextAttachments,
        status: 'proposal_contract',
        project_stage: 'proposal_contract',
        updated_at: new Date().toISOString(),
      })
      .eq('id', sourceBooking.id)
      .select('*')
      .single();
    if (updateError) throw updateError;
    return { booking: { ...sourceBooking, ...(data || {}), attachments: nextAttachments }, attachment };
  };

  const submitProposalEdits = async () => {
    if (!booking?.id || isSavingProposal || pacViewOnly) return;
    setIsSavingProposal(true);
    setProposalError('');
    const pendingChanges = pickProposalEditValues(proposalForm);
    const { data, error: updateError } = await supabase
      .from('bookings')
      .update({
        proposal_pending_changes: pendingChanges,
        proposal_review_status: PROPOSAL_REVIEW_STATUSES.PENDING_ADMIN,
        status: 'proposal_contract',
        project_stage: 'proposal_contract',
        updated_at: new Date().toISOString(),
      })
      .eq('id', booking.id)
      .select('*')
      .single();
    if (updateError) {
      setProposalError(updateError.message || 'Unable to submit proposal edits.');
      setIsSavingProposal(false);
      return;
    }
    const updatedBooking = { ...booking, ...(data || {}) };
    setBooking(updatedBooking);
    setIsSavingProposal(false);
    try {
      await createAdminBookingNotification(updatedBooking, {
        title: `Proposal edits submitted: ${updatedBooking.event_name || updatedBooking.request_code}`,
        message: `${updatedBooking.requester_name || 'Client'} submitted edited proposal details for review. Booking #${updatedBooking.id}.`,
        meta: `Bookings #${updatedBooking.id}`,
        scope: 'admin',
      });
    } catch (notificationError) {
      console.warn('Unable to notify admin about proposal edits:', notificationError?.message || notificationError);
    }
  };

  const submitAgreementConfirmation = async () => {
    if (!booking?.id || !agreementChecked || isSavingProposal || pacViewOnly) return;
    if (!detailsApproved) {
      setProposalError('An admin must approve the proposal details before you can agree.');
      return;
    }
    setIsSavingProposal(true);
    setProposalError('');
    const nowIso = new Date().toISOString();
    const { data, error: updateError } = await supabase
      .from('bookings')
      .update({
        client_agreement_confirmed: true,
        client_agreement_confirmed_at: nowIso,
        status: 'proposal_contract',
        project_stage: 'proposal_contract',
        updated_at: nowIso,
      })
      .eq('id', booking.id)
      .select('*')
      .single();
    if (updateError) {
      setProposalError(updateError.message || 'Unable to confirm agreement.');
      setIsSavingProposal(false);
      return;
    }
    let updatedBooking = { ...booking, ...(data || {}) };
    try {
      const stored = await storeGeneratedContractPdf(updatedBooking);
      updatedBooking = stored.booking;
    } catch (pdfError) {
      console.error('Contract PDF storage failed:', pdfError);
      setProposalError(pdfError.message || 'Agreement saved, but the contract PDF could not be stored.');
    }
    setBooking(updatedBooking);
    setIsSavingProposal(false);
    try {
      await createAdminBookingNotification(updatedBooking, {
        title: `Client agreed: ${updatedBooking.event_name || updatedBooking.request_code}`,
        message: `${updatedBooking.requester_name || 'Client'} agreed to the proposal details. Booking #${updatedBooking.id}.`,
        meta: `Bookings #${updatedBooking.id}`,
        scope: 'admin',
      });
    } catch (notificationError) {
      console.warn('Unable to notify admin about agreement:', notificationError?.message || notificationError);
    }
  };

  const resolveContractUrl = (attachment) => {
    const file = attachment || storedContract;
    if (!file) return '';
    if (file.url) return file.url;
    if (!file.path) return '';
    return supabase.storage.from(CONTRACT_BUCKET).getPublicUrl(file.path).data?.publicUrl || '';
  };

  const viewStoredContract = (attachment) => {
    const url = resolveContractUrl(attachment);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const downloadStoredContract = (attachment) => {
    const file = attachment || storedContract;
    const url = resolveContractUrl(file);
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = file?.name || 'EchoJham_Contract.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const submitSignedContract = async () => {
    if (!booking?.id || !contractFile || isSubmittingContract || pacViewOnly) return;
    if (!booking?.client_agreement_confirmed && !booking?.agreement_confirmed) {
      setContractError('You must agree to the proposal details before uploading a signed contract.');
      return;
    }
    if (!isPdfFile(contractFile)) {
      setContractError('Please upload a PDF file only.');
      return;
    }
    if (!canClientReplaceSignedContract(booking)) {
      setContractError('The accepted contract is locked and cannot be replaced.');
      return;
    }

    setIsSubmittingContract(true);
    setContractError('');
    const nowIso = new Date().toISOString();
    const storagePath = `${booking.id}/${Date.now()}-${contractFile.name}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from(CONTRACT_BUCKET)
        .upload(storagePath, contractFile, {
          upsert: true,
          contentType: 'application/pdf',
        });
      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage.from(CONTRACT_BUCKET).getPublicUrl(storagePath);
      const signedContractUrl = publicUrlData?.publicUrl || '';
      const contractAttachment = {
        type: SIGNED_CONTRACT_TYPE,
        name: contractFile.name,
        path: storagePath,
        url: signedContractUrl,
        content_type: 'application/pdf',
        uploaded_at: nowIso,
      };
      const nextAttachments = upsertAttachment(booking.attachments, contractAttachment);
      const { data, error: updateError } = await supabase
        .from('bookings')
        .update({
          attachments: nextAttachments,
          signed_contract_url: signedContractUrl,
          signed_contract_path: storagePath,
          contract_review_status: CONTRACT_REVIEW_STATUSES.PENDING_ADMIN,
          contract_revision_message: null,
          status: 'proposal_contract',
          project_stage: 'proposal_contract',
          updated_at: nowIso,
        })
        .eq('id', booking.id)
        .select('*')
        .single();
      if (updateError) throw updateError;

      const notifyBooking = { ...booking, ...(data || {}), attachments: nextAttachments, signed_contract_url: signedContractUrl, signed_contract_path: storagePath };
      setBooking(notifyBooking);
      setContractFile(null);
      await createAdminBookingNotification(notifyBooking, {
        title: `Signed contract uploaded: ${notifyBooking.event_name || notifyBooking.request_code}`,
        message: `${notifyBooking.requester_name || 'Client'} uploaded the signed contract PDF. Booking #${notifyBooking.id}.`,
        meta: signedContractUrl || `File: ${contractFile.name}`,
        scope: 'admin',
      });
    } catch (uploadError) {
      console.error('Signed contract upload failed:', uploadError);
      setContractError(uploadError.message || 'Unable to upload signed contract. Please try again.');
    } finally {
      setIsSubmittingContract(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-11rem)] text-white">
      <div className="mx-auto w-full max-w-md lg:max-w-2xl px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/book')}
            className="p-2 hover:bg-white/10 rounded-lg transition"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold">Event Booking Progress</h1>
        </div>

        {/* Event Info */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <p className="text-sm text-slate-300 mb-1">Event Name:</p>
          <p className="text-lg font-bold text-white mb-3">{booking.event_name || 'Untitled Event'}</p>
          
          <p className="text-sm text-slate-300 mb-1">Date Submitted:</p>
          <p className="text-sm text-slate-200">
            {formatSubmittedDateTime(booking.created_at)}
          </p>
          <button
            type="button"
            onClick={() => setShowBookingDetailsModal(true)}
            className="mt-4 w-full rounded-md border border-white/30 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
          >
            View Booking Details
          </button>
        </div>

        {/* Status Badge */}
        <div className="mb-8">
          {isDeclined ? (
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 font-semibold">
                <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
                Rejected
              </div>
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
                <p className="font-semibold mb-1">This booking was declined.</p>
                <p>{booking.decline_reason || 'No reason was provided.'}</p>
              </div>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-300 font-semibold">
              <span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
              In Progress
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="relative">
          {stageKeys.map((stageKey, index) => {
            const stage = timelineStages[stageKey];
            const Icon = stage.icon;
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            return (
              <div key={stageKey} className="relative mb-8 last:mb-0">
                {/* Vertical line connector */}
                {index !== stageKeys.length - 1 && (
                  <div
                    className={`absolute left-6 top-12 h-12 w-0.5 ${
                      isCompleted || isCurrent ? 'bg-emerald-500' : 'bg-slate-600'
                    }`}
                  />
                )}

                {/* Timeline node */}
                <div className="flex items-start gap-4">
                  <div
                    className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 shrink-0 ${
                      isCompleted
                        ? 'bg-emerald-500 border-emerald-500'
                        : isCurrent
                        ? `${stage.color} border-current`
                        : 'bg-slate-700 border-slate-600'
                    }`}
                  >
                    <Icon size={20} className="text-white" />
                  </div>

                  {/* Stage content */}
                  <div className="flex-1 pt-1">
                    <h3
                      className={`font-bold text-base mb-1 ${
                        isCurrent || isCompleted ? 'text-white' : 'text-slate-400'
                      }`}
                    >
                      {stageKey === 'event_live' ? getFinalStageLabel(booking) : stage.label}
                    </h3>
                    {isCurrent && (
                      <p className="text-sm text-slate-300">
                        {stageKey === 'admin_review' && 'Our team is reviewing your request...'}
                        {stageKey === 'initial_consultation' && getConsultationStatusDescription(consultationStatus, booking?.consultation_scheduled_at)}
                        {stageKey === 'proposal_contract' && (
                          pacComplete || contractAccepted
                            ? 'Contract Accepted. Waiting for admin confirmation.'
                            : contractReviewStatus === CONTRACT_REVIEW_STATUSES.NEEDS_REVISION
                              ? 'Contract Needs Revision. Open Proposal & Contract to replace the signed PDF.'
                              : contractReviewStatus === CONTRACT_REVIEW_STATUSES.PENDING_ADMIN
                                ? 'Signed contract submitted. Awaiting admin review.'
                            : detailsApproved
                              ? (booking?.client_agreement_confirmed ? 'Download the contract PDF, sign it, and upload the signed copy.' : 'Review the details and check I Agree to continue.')
                              : (booking?.proposal_review_status === PROPOSAL_REVIEW_STATUSES.PENDING_ADMIN ? 'Your edits are waiting for admin review.' : 'Review and submit the finalized proposal details.')
                        )}
                        {stageKey === 'event_preparation' && 'Pending'}
                        {stageKey === 'event_live' && getFinalStageHelperText(booking)}
                      </p>
                    )}
                  </div>

                  {stageKey === 'proposal_contract' && showPacIcon && !isDeclined && (
                    <button
                      type="button"
                      onClick={openPacModal}
                      className="mt-1 h-7 w-7 rounded-full bg-purple-500 text-white flex items-center justify-center hover:bg-purple-400 transition shrink-0"
                      title={(pacViewOnly || pacComplete) ? 'View Contract' : 'Proposal & Contract'}
                      aria-label={(pacViewOnly || pacComplete) ? 'View Contract' : 'Proposal & Contract'}
                    >
                      <FileText size={14} />
                    </button>
                  )}

                  {stageKey === 'initial_consultation' && currentStage === 'initial_consultation' && !isDeclined && (
                    <button
                      type="button"
                      onClick={openConsultationModal}
                      className="mt-1 h-7 w-7 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-400 transition shrink-0"
                      title="Schedule Consultation"
                      aria-label="Schedule Consultation"
                    >
                      <Calendar size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action button */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <button
            onClick={() => navigate('/book')}
            className="w-full px-4 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition"
          >
            Back to Bookings
          </button>
        </div>

        {showBookingDetailsModal && (
          <div className="fixed inset-0 z-50 bg-black flex items-center justify-center px-4 py-6">
            <div className="w-full max-w-[360px] max-h-[90vh] overflow-y-auto scrollbar-hide bg-black text-white">
              <div className="flex items-start justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-2xl font-bold leading-tight text-white">Booking Details</h3>
                  <p className="mt-1 text-sm text-[#828182]">Here's the information you've submitted.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowBookingDetailsModal(false)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#ff2c71] text-white hover:bg-[#e02664]"
                  aria-label="Close booking details"
                >
                  <X size={16} />
                </button>
              </div>

              <p className="text-sm font-bold uppercase tracking-wide text-white">Submitted Information</p>
              <div className="mt-2 mb-4 h-px w-full bg-[#ff2c71]" />

              <div className="space-y-2.5">
                {buildSubmittedBookingDetails(booking).map((item) => {
                  const Icon = getSubmittedDetailIcon(item.label);
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 rounded-[12px] border border-[#828182] bg-[#1f2126] px-3 py-2.5"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#ff2c71] text-white">
                        <Icon size={13} />
                      </span>
                      <p className="min-w-0 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-white">
                        {item.label}
                      </p>
                      <p className={`min-w-0 flex-1 text-right text-sm text-white break-words ${item.multiline ? 'whitespace-pre-wrap' : ''}`}>
                        {item.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {showConsultationModal && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4 py-6">
            <div className="w-full max-w-md rounded-3xl border border-white/20 bg-[#15171d] p-6 text-white shadow-2xl">
              <div className="flex items-start justify-between gap-3 mb-4">
                <h3 className="text-2xl font-bold leading-tight">Initial Consultation</h3>
                <button
                  type="button"
                  onClick={() => {
                    if (isSubmittingConsultation) return;
                    setShowConsultationModal(false);
                  }}
                  className="text-white/80 hover:text-white"
                  aria-label="Close consultation schedule"
                >
                  <X size={22} />
                </button>
              </div>

              <p className="text-sm text-white/70 leading-relaxed mb-4">
                {getConsultationStatusDescription(consultationStatus, booking?.consultation_scheduled_at)}
              </p>

              <div className="mb-4 rounded-xl border border-white/10 bg-black/20 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Status</p>
                <p className="mt-1 text-sm font-semibold text-white">
                  {getConsultationStatusLabel(consultationStatus)}
                </p>
                {booking?.consultation_scheduled_at ? (
                  <p className="mt-2 text-sm text-slate-300">
                    {consultationIsReadOnly ? 'Confirmed for: ' : 'Current schedule: '}
                    {formatConsultationDateTime(booking.consultation_scheduled_at)}
                  </p>
                ) : null}
              </div>

              {String(booking?.meeting_place || '').trim() ? (
                <div className="mb-4 rounded-xl border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Meeting Place</p>
                  <p className="mt-1 text-sm text-white">{String(booking.meeting_place).trim()}</p>
                </div>
              ) : null}

              {String(booking?.meeting_instructions || '').trim() ? (
                <div className="mb-4 rounded-xl border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Meeting Instructions</p>
                  <p className="mt-1 text-sm text-slate-300 whitespace-pre-wrap">
                    {String(booking.meeting_instructions).trim()}
                  </p>
                </div>
              ) : null}

              {!consultationIsReadOnly ? (
                <label className="block text-sm font-semibold text-white">
                  Preferred date and time
                  <input
                    type="datetime-local"
                    value={consultationDateTime}
                    onChange={(event) => {
                      setConsultationError('');
                      setConsultationDateTime(event.target.value);
                    }}
                    disabled={isSubmittingConsultation}
                    className="mt-2 w-full rounded-lg border border-white/20 bg-[#15171d] px-3 py-2 text-sm text-white focus:border-blue-400 focus:outline-none disabled:opacity-60"
                  />
                </label>
              ) : null}

              {consultationError ? (
                <p className="mt-3 text-sm text-red-400">{consultationError}</p>
              ) : null}

              <div className={`mt-5 ${consultationIsReadOnly ? '' : 'grid grid-cols-2 gap-3'}`}>
                <button
                  type="button"
                  onClick={() => {
                    if (isSubmittingConsultation) return;
                    setShowConsultationModal(false);
                  }}
                  className={`rounded-md border border-white/30 py-2.5 text-sm text-white/90 hover:bg-white/10 ${
                    consultationIsReadOnly ? 'w-full' : ''
                  }`}
                  disabled={isSubmittingConsultation}
                >
                  {consultationIsReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!consultationIsReadOnly ? (
                  <button
                    type="button"
                    onClick={submitConsultationSchedule}
                    disabled={isSubmittingConsultation || !consultationDateTime}
                    className={`rounded-md py-2.5 text-sm font-bold ${
                      isSubmittingConsultation || !consultationDateTime
                        ? 'bg-blue-900/40 text-blue-200/50 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-400'
                    }`}
                  >
                    {isSubmittingConsultation
                      ? 'Submitting...'
                      : consultationStatus === CONSULTATION_STATUSES.SUBMITTED
                        ? 'Update Schedule'
                        : 'Submit'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {showPacModal && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4 py-6">
            <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-3xl border border-white/20 bg-[#15171d] p-6 text-white shadow-2xl">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-2xl font-bold leading-tight">
                    {pacViewOnly || pacComplete ? 'View Contract' : 'Proposal & Contract'}
                  </h3>
                  <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/50">
                    {pacViewOnly || pacComplete ? 'Stored contract PDF' : 'Review, agree, and sign'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (isSavingProposal || isSubmittingContract) return;
                    setShowPacModal(false);
                  }}
                  className="text-white/80 hover:text-white"
                  aria-label="Close proposal and contract"
                >
                  <X size={22} />
                </button>
              </div>

              {(pacViewOnly || pacComplete) ? (
                <>
                  <p className="text-sm text-white/70 leading-relaxed mb-4">
                    {contractAccepted
                      ? 'Contract Accepted. You can view or download the stored signed contract. It cannot be replaced.'
                      : 'Your signed contract is stored with this booking. You can view or download it at any time.'}
                  </p>
                  {storedContract ? (
                    <p className="mb-4 text-sm font-semibold text-white break-all">{storedContract.name || 'Contract PDF'}</p>
                  ) : (
                    <p className="mb-4 text-sm text-white/60">No stored contract file was found.</p>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => viewStoredContract(storedContract)}
                      disabled={!storedContract}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-black py-2.5 text-sm font-bold hover:bg-white/90 disabled:opacity-40"
                    >
                      <FileText size={16} /> View Contract
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadStoredContract(storedContract)}
                      disabled={!storedContract}
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 py-2.5 text-sm text-white/90 hover:bg-white/10 disabled:opacity-40"
                    >
                      <Download size={16} /> Download
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-white/70 leading-relaxed mb-4">
                    {waitingAdminReview
                      ? 'Your edited details are waiting for admin review.'
                      : detailsApproved
                        ? 'Proposal details are approved. Check I Agree, then download and upload the signed PDF.'
                        : 'Review and submit the finalized proposal details. Name and email cannot be changed.'}
                  </p>

                  <div className="max-h-[36vh] space-y-3 overflow-y-auto pr-1 pb-1 scrollbar-hide mb-4">
                    {[
                      { key: 'event_name', label: 'Event Name' },
                      ...(String(booking?.booking_type || '').toLowerCase() === 'corporate'
                        ? [{ key: 'event_type', label: 'Event Type' }, { key: 'attendee_count', label: 'Attendee Count' }]
                        : []),
                      { key: 'theme', label: 'Theme' },
                      { key: 'venue', label: 'Venue' },
                      { key: 'budget_range', label: 'Budget Range' },
                    ].map((field) => (
                      <label key={field.key} className="block text-sm font-semibold text-white">
                        <span className="mb-1.5 block">{field.label}</span>
                        {detailsEditable ? (
                          <input
                            value={proposalForm[field.key] ?? ''}
                            onChange={(event) => setProposalForm((prev) => ({ ...prev, [field.key]: event.target.value }))}
                            className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90"
                          />
                        ) : (
                          <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90">
                            {formatDisplayValue(proposalForm[field.key])}
                          </div>
                        )}
                      </label>
                    ))}

                    {String(booking?.booking_type || '').toLowerCase() !== 'corporate' ? (
                      ['technical_rider', 'musical_director', 'food'].map((vendorKey) => (
                        <label key={vendorKey} className="block text-sm font-semibold text-white">
                          <span className="mb-1.5 block">{vendorKey.replace('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase())}</span>
                          {detailsEditable ? (
                            <input
                              value={proposalForm.concert_vendor_choices?.[vendorKey] || ''}
                              onChange={(event) => setProposalForm((prev) => ({
                                ...prev,
                                concert_vendor_choices: {
                                  ...(prev.concert_vendor_choices || {}),
                                  [vendorKey]: event.target.value,
                                },
                              }))}
                              className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90"
                            />
                          ) : (
                            <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90">
                              {formatDisplayValue(proposalForm.concert_vendor_choices?.[vendorKey] || 'Let EchoJham Provide')}
                            </div>
                          )}
                        </label>
                      ))
                    ) : null}

                    <label className="block text-sm font-semibold text-white">
                      <span className="mb-1.5 block">Additional Details</span>
                      {detailsEditable ? (
                        <textarea
                          value={proposalForm.details || proposalForm.specific_needs || ''}
                          onChange={(event) => setProposalForm((prev) => ({
                            ...prev,
                            details: event.target.value,
                            specific_needs: event.target.value,
                          }))}
                          rows={4}
                          className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90"
                        />
                      ) : (
                        <div className="rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white/90 whitespace-pre-wrap min-h-[84px]">
                          {formatDisplayValue(proposalForm.details || proposalForm.specific_needs || 'No additional details provided.')}
                        </div>
                      )}
                    </label>
                  </div>

                  {detailsEditable ? (
                    <button
                      type="button"
                      onClick={submitProposalEdits}
                      disabled={isSavingProposal}
                      className="w-full mb-4 rounded-md bg-[#b10808] text-white py-2.5 text-sm font-bold hover:bg-[#c90a0a] disabled:opacity-50"
                    >
                      {isSavingProposal ? 'Submitting...' : 'Submit Details for Admin Review'}
                    </button>
                  ) : null}

                  {canAgree ? (
                    <>
                      <div className="space-y-3 mb-4">
                        {AGREEMENT_TERMS.map((item) => {
                          const TermIcon = item.icon;
                          return (
                            <div key={item.text} className="flex items-start gap-3">
                              <span className="text-red-500 mt-0.5">
                                <TermIcon size={18} />
                              </span>
                              <p className="text-sm text-white/90 leading-snug">{item.text}</p>
                            </div>
                          );
                        })}
                      </div>
                      <label className="flex items-center gap-3 mb-4 text-xs text-white/70">
                        <input
                          type="checkbox"
                          checked={agreementChecked}
                          onChange={(event) => setAgreementChecked(event.target.checked)}
                          className="w-4 h-4 flex-shrink-0 rounded border-white/30 cursor-pointer accent-brand-maroon"
                          disabled={isSavingProposal}
                        />
                        <span className="leading-relaxed">I Agree to the finalized proposal details and contract terms.</span>
                      </label>
                      <button
                        type="button"
                        onClick={submitAgreementConfirmation}
                        disabled={!agreementChecked || isSavingProposal}
                        className={`w-full mb-4 rounded-md py-2.5 text-sm font-bold ${
                          !agreementChecked || isSavingProposal
                            ? 'bg-red-900/40 text-red-200/50 cursor-not-allowed'
                            : 'bg-[#b10808] text-white hover:bg-[#c90a0a]'
                        }`}
                      >
                        {isSavingProposal ? 'Saving...' : 'Confirm I Agree'}
                      </button>
                    </>
                  ) : null}

                  {clientHasAgreed && (contractTemplate || storedContract) ? (
                    <div className="mb-4 grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => viewStoredContract(contractAccepted ? storedContract : (contractTemplate || storedContract))}
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-white text-black py-2.5 text-sm font-bold hover:bg-white/90"
                      >
                        <FileText size={16} /> View Contract
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadStoredContract(contractAccepted ? storedContract : (contractTemplate || storedContract))}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 py-2.5 text-sm text-white/90 hover:bg-white/10"
                      >
                        <Download size={16} /> Download
                      </button>
                    </div>
                  ) : null}

                  {clientHasAgreed ? (
                    <p className="mb-3 text-xs font-semibold text-white/80">
                      {getOfficialContractTitle(booking)} • {getContractReviewLabel(booking)}
                    </p>
                  ) : null}

                  {contractReviewStatus === CONTRACT_REVIEW_STATUSES.NEEDS_REVISION && booking?.contract_revision_message ? (
                    <div className="mb-4 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                      <p className="font-semibold mb-1">Contract Needs Revision</p>
                      <p className="text-amber-50/90">{booking.contract_revision_message}</p>
                    </div>
                  ) : null}

                  {canUploadSigned ? (
                    <>
                      {signedUploaded ? (
                        <p className="mb-3 text-xs text-white/60 break-all">
                          Current signed file: {storedContract?.name || booking?.signed_contract_path || 'Signed contract PDF'}
                        </p>
                      ) : null}
                      <label className="block text-sm font-semibold text-white/90 mb-2">
                        {signedUploaded ? 'Replace Signed Contract PDF' : 'Signed Contract PDF'}
                      </label>
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(event) => setContractFile(event.target.files?.[0] || null)}
                        className="block w-full text-sm text-white/80 file:mr-4 file:rounded-md file:border-0 file:bg-[#b10808] file:px-4 file:py-2 file:text-white file:font-semibold hover:file:bg-[#c90a0a]"
                        disabled={isSubmittingContract}
                      />
                      {contractFile ? <p className="mt-2 text-xs text-white/60">Selected: {contractFile.name}</p> : null}
                      <button
                        type="button"
                        onClick={submitSignedContract}
                        disabled={!contractFile || isSubmittingContract}
                        className={`mt-3 w-full rounded-md py-2.5 text-sm font-bold ${
                          !contractFile || isSubmittingContract
                            ? 'bg-indigo-900/40 text-indigo-200/50 cursor-not-allowed'
                            : 'bg-[#b10808] text-white hover:bg-[#c90a0a]'
                        }`}
                      >
                        {isSubmittingContract
                          ? 'Uploading...'
                          : signedUploaded
                            ? 'Resubmit Signed Contract PDF'
                            : 'Upload Signed Contract PDF'}
                      </button>
                    </>
                  ) : null}

                  {proposalError ? <p className="mt-3 text-xs text-red-400">{proposalError}</p> : null}
                  {contractError ? <p className="mt-3 text-xs text-red-400">{contractError}</p> : null}

                  <button
                    type="button"
                    onClick={() => setShowPacModal(false)}
                    className="mt-4 w-full rounded-md border border-white/30 py-2.5 text-sm text-white/90 hover:bg-white/10"
                    disabled={isSavingProposal || isSubmittingContract}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
